// src/pages/Cart.jsx
import { useEffect, useMemo, useState } from 'react'
import { getCart, setCart, removeFromCart, clearCart } from '../lib/cartStore'
import { fetchProduct } from '../lib/products'
import { Link, useSearchParams } from 'react-router-dom'

const PLACEHOLDER =
  'https://via.placeholder.com/200x200?text=Image+indisponible'

// 📌 NUMÉRO WHATSAPP DU SHOP (à adapter si besoin)
const WHATSAPP_PHONE = '22899740138'

export default function Cart() {
  const [lines, setLines] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // ✅ lire les paramètres d'URL : ?payment=failed&ref=...
  const [searchParams] = useSearchParams()
  const paymentStatus = searchParams.get('payment')
  const paymentRef = searchParams.get('ref')

  // Charger les infos produits du panier
  useEffect(() => {
    ;(async () => {
      try {
        setLoading(true)
        setError(null)

        const cart = getCart() || []
        if (!cart.length) {
          setLines([])
          return
        }

        const enriched = await Promise.all(
          cart.map(async (it) => {
            try {
              const p = await fetchProduct(it.pid)
              return { ...it, product: p || null }
            } catch {
              return { ...it, product: null }
            }
          })
        )

        setLines(enriched)
      } catch (e) {
        setError(e?.message || 'Erreur lors du chargement du panier.')
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  // Total & nombre d'articles
  const { total, totalQty } = useMemo(() => {
    return lines.reduce(
      (acc, l) => {
        const price = Number(l.product?.price || 0)
        acc.total += price * (l.qty || 0)
        acc.totalQty += l.qty || 0
        return acc
      },
      { total: 0, totalQty: 0 }
    )
  }, [lines])

  // Met à jour la quantité d'une ligne
  function updateQty(pid, rawQty) {
    const qty = Number.isFinite(rawQty) ? Math.max(1, rawQty) : 1

    const updated = lines.map((l) =>
      l.pid === pid ? { ...l, qty } : l
    )
    setLines(updated)

    const saved = updated.map((l) => ({
      pid: l.pid,
      vendorId: l.vendorId,
      qty: l.qty,
    }))
    setCart(saved)
  }

  // Helpers pour les boutons - / +
  function inc(pid) {
    const line = lines.find((l) => l.pid === pid)
    if (!line) return
    updateQty(pid, (line.qty || 0) + 1)
  }

  function dec(pid) {
    const line = lines.find((l) => l.pid === pid)
    if (!line) return
    const next = (line.qty || 0) - 1
    if (next <= 0) {
      handleRemove(pid)
    } else {
      updateQty(pid, next)
    }
  }

  function handleRemove(pid) {
    removeFromCart(pid)
    setLines((prev) => prev.filter((l) => l.pid !== pid))
  }

  function handleClear() {
    clearCart()
    setLines([])
  }

  // ✅ Validation via WhatsApp
  function handleCheckoutWhatsApp() {
    if (!lines.length) return

    // Construire un petit récap du panier
    const header = 'Bonjour, je souhaite confirmer ma commande GoShop :\n'
    const body = lines
      .map((l, idx) => {
        const p = l.product
        const title =
          p?.title ||
          p?.Name ||
          p?.name ||
          'Produit'
        const price = Number(p?.price || 0)
        const lineTotal = price * (l.qty || 0)
        return `${idx + 1}. ${title} — ${l.qty} x ${price.toLocaleString()} XOF = ${lineTotal.toLocaleString()} XOF`
      })
      .join('\n')

    const footer = `\n\nTotal estimé : ${total.toLocaleString()} XOF\nMerci de me confirmer la disponibilité et le mode de livraison.`

    const fullText = header + body + footer
    const url = `https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent(
      fullText
    )}`

    // Ouvre WhatsApp dans un nouvel onglet
    window.open(url, '_blank')

    // Vide le panier côté client
    handleClear()
  }

  if (loading) {
    return (
      <div className="my-4">
        <h3 className="mb-3">Panier</h3>
        <div className="alert alert-info">Chargement du panier…</div>
      </div>
    )
  }

  return (
    <div className="cart-page">
      {/* ⚠️ Message si PayGate a renvoyé un échec */}
      {paymentStatus === 'failed' && (
        <div className="alert alert-warning">
          <div className="fw-semibold mb-1">
            Le paiement n’a pas été confirmé.
          </div>
          <div className="small">
            Vous pouvez réessayer ou choisir un autre mode de paiement.
            {paymentRef && (
              <>
                <br />
                <span className="text-muted">
                  Référence paiement : <strong>{paymentRef}</strong>
                </span>
              </>
            )}
          </div>
        </div>
      )}

      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">Panier</h3>
        {totalQty > 0 && (
          <div className="small text-muted">
            {totalQty} article(s)
          </div>
        )}
      </div>

      {error && (
        <div className="alert alert-danger mb-3">
          {error}
        </div>
      )}

      {lines.length === 0 && !loading && (
        <div className="alert alert-light border">
          <div className="fw-semibold mb-1">
            Votre panier est vide.
          </div>
          <div className="small text-muted mb-2">
            Parcourez le catalogue et ajoutez vos produits.
          </div>
          <Link to="/catalog" className="btn btn-primary btn-sm">
            Voir le catalogue
          </Link>
        </div>
      )}

      {/* Lignes du panier */}
      {lines.map((l) => {
        const p = l.product
        const price = Number(p?.price || 0)
        const lineTotal = price * (l.qty || 0)

        const img =
          p?.imageUrl ||
          p?.images?.[0] ||
          p?.ImageUrl?.[0] ||
          PLACEHOLDER

        const title =
          p?.title ||
          p?.Name ||
          p?.name ||
          'Produit'

        return (
          <div className="card-modern p-3 mb-2" key={l.pid}>
            <div className="d-flex align-items-center gap-3">
              {/* Image + lien vers la fiche produit si possible */}
              <Link
                to={p ? `/ip/${encodeURIComponent(title)}/${l.pid}` : '#'}
                className="d-block"
                style={{ textDecoration: 'none' }}
              >
                <div
                  style={{
                    width: 90,
                    height: 90,
                    borderRadius: 12,
                    overflow: 'hidden',
                    background: '#0b1226',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <img
                    src={img}
                    alt={title}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                    }}
                    onError={(e) => {
                      e.currentTarget.src = PLACEHOLDER
                    }}
                  />
                </div>
              </Link>

              {/* Infos produit */}
              <div className="flex-fill">
                <div className="fw-semibold mb-1">
                  {title}
                </div>
                <div className="small text-muted">
                  {price.toLocaleString()} XOF / unité
                </div>
              </div>

              {/* Contrôle quantité type - qty + */}
              <div className="d-flex flex-column align-items-end gap-2">
                <div className="qty-pill d-flex align-items-center">
                  <button
                    type="button"
                    className="qty-btn"
                    onClick={() => dec(l.pid)}
                  >
                    −
                  </button>
                  <span className="qty-value">
                    {l.qty}
                  </span>
                  <button
                    type="button"
                    className="qty-btn"
                    onClick={() => inc(l.pid)}
                  >
                    +
                  </button>
                </div>

                {/* Sous-total & bouton supprimer */}
                <div className="text-end">
                  <div className="fw-semibold">
                    {lineTotal.toLocaleString()} XOF
                  </div>
                  <button
                    className="btn btn-link btn-sm text-danger p-0 mt-1"
                    onClick={() => handleRemove(l.pid)}
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      })}

      {/* Résumé & actions bas de page */}
      {lines.length > 0 && (
        <div className="card-modern p-3 mt-3">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
            <div>
              <div className="small text-muted">
                Total ({totalQty} article(s))
              </div>
              <div className="h5 mb-0">
                {total.toLocaleString()} XOF
              </div>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <button
                className="btn btn-soft"
                type="button"
                onClick={handleClear}
              >
                Vider le panier
              </button>
              <button
                className="btn btn-success"
                type="button"
                onClick={handleCheckoutWhatsApp}
              >
                Valider via WhatsApp
              </button>
              <Link
                className="btn btn-primary"
                to="/checkout"
              >
                Passer la commande
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
